/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import React, { useMemo } from "react";
import type { CleanedBlobRes } from "@cosmos/common";
import { CosmosTable } from "@components/elements/atoms/table";
import { CosmosText } from "@components/elements/atoms/text";
import { CosmosButton } from "@components/elements/atoms";
import { useMessages } from "@epic-mw/i18n";
import { formatFileSize, formatDate, isValidUrl } from "./BlobTable.utils";

export interface BlobTableProps {
	/** Array of cleaned blob data to display in the table */
	blobs: CleanedBlobRes[];
}

/**
 * BlobTable organism component that renders a table of blob files
 * Matches the structure from CosmosTable.stories.tsx BlobTable story
 */
export const BlobTable = ({ blobs }: BlobTableProps): React.JSX.Element => {
	const messages = useMessages();
	const sortedBlobs = useMemo(() => {
		return blobs
			.filter((blob) => blob.size > 0)
			.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
	}, [blobs]);

	return (
		<CosmosTable gridTemplateColumns="minmax(max-content, 1fr) max-content max-content max-content">
			<CosmosTable.Header>
				<CosmosTable.Column>
					<CosmosText
						variant="uiMD"
						color="eds.text.secondary"
						text={messages["epic.cosmos.blob.filename"] || "File name"}
					/>
				</CosmosTable.Column>
				<CosmosTable.Column>
					<CosmosText variant="uiMD" color="eds.text.secondary" text={messages["epic.cosmos.blob.size"] || "Size"} />
				</CosmosTable.Column>
				<CosmosTable.Column>
					<CosmosText
						variant="uiMD"
						color="eds.text.secondary"
						text={messages["epic.cosmos.blob.dateUploaded"] || "Date uploaded"}
					/>
				</CosmosTable.Column>
				<CosmosTable.Column>
					<CosmosText
						variant="uiMD"
						color="eds.text.secondary"
						text={messages["epic.cosmos.blob.download.header"] || "Link"}
					/>
				</CosmosTable.Column>
			</CosmosTable.Header>
			<CosmosTable.Body w="full">
				{sortedBlobs.map((blob, index) => (
					<CosmosTable.Row key={`${blob.name}-${index}`}>
						<CosmosTable.Cell gap="eds.dimension.8">
							<CosmosText variant="uiMD" color="eds.text.primary" text={blob.name} />
						</CosmosTable.Cell>
						<CosmosTable.Cell>
							<CosmosText variant="uiMD" color="eds.text.primary" text={formatFileSize(blob.size)} />
						</CosmosTable.Cell>
						<CosmosTable.Cell>
							<CosmosText variant="uiMD" color="eds.text.primary" text={formatDate(blob.createdAt)} />
						</CosmosTable.Cell>
						<CosmosTable.Cell>
							{isValidUrl(blob.downloadUrl) ? (
								<CosmosButton
									href={blob.downloadUrl}
									label={messages["epic.cosmos.blob.download"] || "Download"}
									variant="secondary"
									size="sm"
									name={blob.name}
								/>
							) : null}
						</CosmosTable.Cell>
					</CosmosTable.Row>
				))}
			</CosmosTable.Body>
		</CosmosTable>
	);
};
